import {
  AfriexAdminPermission,
  AfriexCurrencyCode,
} from '@afriex/core/domain/enums';

import {Button, ChannelProcessorDisplay, Tabs} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {EUFlag} from '@/constants/countries';
import {getChannelProcessors} from '@/constants/formatters';
import type {ProcessorCapabilityMatrix} from '@/services/processorService';
import type {
  AfriexCountry,
  AfriexCountryCode,
  AfriexCurrency,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  TabItem,
} from '@/types';
import {AfriexTransactionType} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {capitalizeWords} from '@/utils/strings';

import type {ProcessorTableProps} from './ProcessorTable';
import type {RemoveProcessorRequest} from './types';
import {extractProcessorListFrom} from './utils';

type SummaryProp = {
  item: AfriexCurrency;
  countryCode: AfriexCountryCode;
  countryList: AfriexCountry[];
  target: 'user' | 'business' | 'api';
  capabilityMatrix: ProcessorCapabilityMatrix;
  showProcessorUpdateForm: ProcessorTableProps['showProcessorUpdateForm'];
  onRemoveProcessor: (req: RemoveProcessorRequest) => void;
};

/**
 * Per-channel card: ChannelProcessorDisplay (currentProcessor + Remove) + Active vailable processors[]
 *
 * Section 1: Current Processor — reuse ChannelProcessorDisplay from Settings/Currency
 */
const ChannelProcessorRow = ({
  item,
  target,
  type,
  channel,
  currentProcessor,
  processors,
  onRemoveProcessor,
  switchedOff,
}: {
  item: AfriexCurrency;
  target: 'user' | 'business' | 'api';
  type: AfriexTransactionType;
  channel: AfriexTransactionChannel;
  currentProcessor: AfriexTransactionProcessor;
  processors: AfriexTransactionProcessor[];
  onRemoveProcessor: (req: RemoveProcessorRequest) => void;
  switchedOff?: ReadonlyMap<string, string>;
}) => (
  <ChannelProcessorDisplay
    channel={channel}
    switchedOff={switchedOff}
    currentProcessor={currentProcessor}
    transactionType={type}
    onRemove={(tt, ch) =>
      onRemoveProcessor({target, currency: item, type: tt, channel: ch})
    }
    activeAvailableProcessors={processors}
    onRemoveAvailableProcessor={(processorName) =>
      onRemoveProcessor({
        target,
        currency: item,
        type,
        channel,
        processorName,
      })
    }
  />
);

/** Why a processor declared for a corridor cannot be selected here. */
const SWITCHED_OFF_NOTES = {
  dev: 'not live in production',
  retired: 'retired',
} as const;

type SwitchedOffByChannel = Map<AfriexTransactionChannel, Map<string, string>>;

/**
 * Type-level footer: Available processor(s) to set, rendered once per tab.
 * Processors the server has switched off for the corridor are listed after the
 * selectable ones, greyed out and labelled why, so the reason is visible
 * before opening the edit form.
 */
const AvailableProcessorsToSet = ({
  processors,
  switchedOff,
}: {
  processors: {
    channel: AfriexTransactionChannel;
    processors: Set<string>;
  }[];
  switchedOff: SwitchedOffByChannel;
}) => {
  const channels = [
    ...new Set([
      ...processors.map(({channel}) => channel),
      ...switchedOff.keys(),
    ]),
  ];
  return (
    <div className="mt-3">
      <h4 className="font-medium text-gray-500 dark:text-gray-400 mb-3 border-b border-gray-300 dark:border-dark-borderHeavy pb-2">
        Available processor(s) to set
      </h4>
      {switchedOff.size > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Greyed out: switched off in this environment and cannot be selected.
          &quot;Not live in production&quot; is built but not launched yet;
          &quot;retired&quot; is no longer used in production.
        </p>
      )}
      {channels.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">NONE</p>
      ) : (
        channels.map((channel) => (
          <div key={channel} className="mb-3">
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {capitalizeWords(channel?.replace(/_/g, ' '))}
              </p>
              <ul className="flex flex-row flex-wrap gap-1">
                {[
                  ...(processors.find((p) => p.channel === channel)
                    ?.processors ?? []),
                ].map((p) => (
                  <li
                    key={p}
                    className="p-1 px-2 rounded bg-gray-200 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300"
                  >
                    {p.toUpperCase()}
                  </li>
                ))}
                {[...(switchedOff.get(channel) ?? [])].map(([p, note]) => (
                  <li
                    key={p}
                    title={`Cannot be selected: ${note}`}
                    className="p-1 px-2 rounded border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-400 dark:text-gray-500"
                  >
                    {p.toUpperCase()} (!) {note}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const ProcessorSummary = ({
  item,
  target,
  countryList,
  countryCode,
  capabilityMatrix,
  showProcessorUpdateForm,
  onRemoveProcessor,
}: SummaryProp) => {
  const depositProcessors = extractProcessorListFrom(item?.[target]?.DEPOSIT);
  const withdrawalProcessors = extractProcessorListFrom(
    item?.[target]?.WITHDRAW,
  );

  const currencyCode = item?.code as AfriexCurrencyCode;
  const isEuroCurrency = item?.code === AfriexCurrencyCode.EUR;
  const flag = isEuroCurrency
    ? EUFlag
    : mapCountryCodeToFlags(countryList)[countryCode];

  const depositCapability = capabilityMatrix?.DEPOSIT;
  const withdrawalCapability = capabilityMatrix?.WITHDRAW;

  const switchedOffFor = (type: AfriexTransactionType) => {
    const byChannel: SwitchedOffByChannel = new Map();
    for (const status of ['dev', 'retired'] as const) {
      for (const {channel, processors} of getChannelProcessors(
        capabilityMatrix?.[status]?.[type],
        currencyCode,
        target,
      )) {
        const notes = byChannel.get(channel) ?? new Map<string, string>();
        processors.forEach((p) => notes.set(p, SWITCHED_OFF_NOTES[status]));
        byChannel.set(channel, notes);
      }
    }
    return byChannel;
  };

  const depositSwitchedOff = switchedOffFor(AfriexTransactionType.DEPOSIT);
  const withdrawalSwitchedOff = switchedOffFor(AfriexTransactionType.WITHDRAW);

  const depositContent = (
    <div className="mt-3">
      {depositProcessors.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No deposit processors configured.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {depositProcessors.map((x) => (
            <ChannelProcessorRow
              key={x.channel}
              item={item}
              target={target}
              type={AfriexTransactionType.DEPOSIT}
              channel={x.channel}
              currentProcessor={x.currentProcessor}
              processors={x.processors}
              onRemoveProcessor={onRemoveProcessor}
              switchedOff={depositSwitchedOff.get(x.channel)}
            />
          ))}
        </div>
      )}
      <AvailableProcessorsToSet
        processors={getChannelProcessors(
          depositCapability,
          currencyCode,
          target,
        )}
        switchedOff={depositSwitchedOff}
      />
    </div>
  );

  const withdrawalContent = (
    <div className="mt-3">
      {withdrawalProcessors.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No withdrawal processors configured.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {withdrawalProcessors.map((x) => (
            <ChannelProcessorRow
              key={x.channel}
              item={item}
              target={target}
              type={AfriexTransactionType.WITHDRAW}
              channel={x.channel}
              currentProcessor={x.currentProcessor}
              processors={x.processors}
              onRemoveProcessor={onRemoveProcessor}
              switchedOff={withdrawalSwitchedOff.get(x.channel)}
            />
          ))}
        </div>
      )}
      <AvailableProcessorsToSet
        processors={getChannelProcessors(
          withdrawalCapability,
          currencyCode,
          target,
        )}
        switchedOff={withdrawalSwitchedOff}
      />
    </div>
  );

  const tabs: TabItem[] = [
    {id: 'deposit', title: 'Deposit', content: depositContent},
    {id: 'withdrawal', title: 'Withdrawal', content: withdrawalContent},
  ];

  return (
    <div className="whitespace-normal break-words bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-2xl mr-3">
          Processors for {currencyCode} {flag}
        </h2>
      </div>
      <Tabs tabs={tabs} />
      <div className="flex items-center justify-end gap-6 mt-4">
        <PermissionsProvider permission={AfriexAdminPermission.CurrencyUpdate}>
          <Button
            data-tip
            data-for="payoutButton"
            onClick={() => showProcessorUpdateForm(item)}
            variant={'solid'}
            colorScheme="cyan"
          >
            Update Processor
          </Button>
        </PermissionsProvider>
      </div>
    </div>
  );
};

export default ProcessorSummary;
